import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { School, Users, GraduationCap } from "lucide-react";

export default function Groups() {
  const { user } = useAuth();
  const [mine, setMine] = useState(null);
  const [all, setAll] = useState({ colleges: [], batches: [] });

  useEffect(() => {
    api.get("/groups/me").then(({ data }) => setMine(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/groups/all").then(({ data }) => setAll(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="groups-page" className="max-w-5xl mx-auto space-y-8">
      <div>
        <div className="label-eyebrow">Groups</div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[color:var(--ayur-emerald)] mt-2">Rediscover your college & batch</h1>
        <p className="text-[color:var(--ayur-ink-2)] mt-2 max-w-2xl">
          Every scholar automatically belongs to a college group and a batch-year group. Step in and find your seniors, juniors and classmates.
        </p>
      </div>

      {mine && (
        <section>
          <div className="label-eyebrow mb-3">Your groups</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link data-testid="my-college-group" to={`/app/groups/college/${encodeURIComponent(mine.college.name)}`}
              className="paper-card p-6 hover:-translate-y-0.5 transition">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[color:var(--ayur-emerald)]/10 flex items-center justify-center">
                  <School className="w-5 h-5 text-[color:var(--ayur-emerald)]" />
                </div>
                <div>
                  <div className="label-eyebrow">College</div>
                  <div className="font-serif-display text-lg text-[color:var(--ayur-emerald)]">{mine.college.name}</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-[color:var(--ayur-mute)] flex items-center gap-1">
                <Users className="w-4 h-4" /> {mine.college.members_count} scholars
              </div>
            </Link>

            <Link data-testid="my-batch-group" to={`/app/groups/batch/${mine.batch.year}`}
              className="paper-card p-6 hover:-translate-y-0.5 transition">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[color:var(--ayur-terracotta)]/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-[color:var(--ayur-terracotta)]" />
                </div>
                <div>
                  <div className="label-eyebrow">Batch</div>
                  <div className="font-serif-display text-lg text-[color:var(--ayur-emerald)]">Batch of {mine.batch.year}</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-[color:var(--ayur-mute)] flex items-center gap-1">
                <Users className="w-4 h-4" /> {mine.batch.members_count} scholars
              </div>
            </Link>
          </div>
        </section>
      )}

      <section>
        <div className="label-eyebrow mb-3">All college groups</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {all.colleges.length === 0 && <div className="text-sm text-[color:var(--ayur-mute)]">No colleges yet.</div>}
          {all.colleges.map((c) => (
            <Link data-testid="college-group-card" key={c.name}
              to={`/app/groups/college/${encodeURIComponent(c.name)}`}
              className="paper-card p-4 flex items-center justify-between hover:bg-white transition">
              <div className="min-w-0">
                <div className="font-semibold text-[color:var(--ayur-ink)] truncate">{c.name}</div>
                <div className="text-xs text-[color:var(--ayur-mute)]">{c.members_count} scholars</div>
              </div>
              <School className="w-5 h-5 text-[color:var(--ayur-emerald)] shrink-0" />
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="label-eyebrow mb-3">Batch-year groups</div>
        <div className="flex flex-wrap gap-2">
          {all.batches.map((b) => (
            <Link data-testid="batch-group-card" key={b.year} to={`/app/groups/batch/${b.year}`}
              className="pill hover:!bg-[color:var(--ayur-emerald)] hover:!text-white transition">
              Batch {b.year} · {b.members_count}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
